import React from 'react';
import PropTypes from 'prop-types';
import { useReactTable, getCoreRowModel, flexRender } from '@tanstack/react-table';
import styles from './DataTable.module.scss';

/* ────────────────────────────────────────────────────────────
 * DataTable — page-level table convention.
 *
 * Two render modes:
 *
 * 1. TanStack mode (preferred for new screens). Pass `data` and
 *    `columns`; the component runs `useReactTable` internally and
 *    renders the head/body via `flexRender`.
 *
 *      <DataTable data={rows} columns={columns} />
 *
 * 2. Legacy children mode (existing screens). Pass raw <thead> /
 *    <tbody> as children — kept so the migration to TanStack can
 *    happen screen-by-screen without breaking the world.
 *
 *      <DataTable>
 *        <thead><tr><th>Date</th>...</tr></thead>
 *        <tbody><tr><td>...</td></tr></tbody>
 *      </DataTable>
 *
 * Owns: scroll wrapper (vertical + horizontal), thin scrollbar,
 * sticky uppercase tinted thead, cell padding, hover row state,
 * border treatment.
 * ──────────────────────────────────────────────────────────── */

const DataTable = ({
  stickyHeader,
  hoverable,
  children,
  className,
  data,
  columns,
  tableOptions,
  emptyMessage,
  onRowClick,
  getRowProps,
}) => {
  const usingTanstack = Array.isArray(data) && Array.isArray(columns);

  const table = useReactTable({
    data: usingTanstack ? data : EMPTY_ARRAY,
    columns: usingTanstack ? columns : EMPTY_ARRAY,
    getCoreRowModel: getCoreRowModel(),
    ...(tableOptions || {}),
  });

  const wrapClass = [styles.wrap, className || ''].filter(Boolean).join(' ');
  const tableClass = [
    styles.table,
    stickyHeader ? styles.sticky : '',
    hoverable ? styles.hoverable : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={wrapClass}>
      <table className={tableClass}>
        {usingTanstack ? (
          <>
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((header) => (
                    <th key={header.id} colSpan={header.colSpan}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className={styles.empty}>
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => {
                  const extra = getRowProps ? getRowProps(row) : null;
                  return (
                    <tr
                      key={row.id}
                      onClick={onRowClick ? () => onRowClick(row.original, row) : undefined}
                      {...(extra || {})}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </>
        ) : (
          children
        )}
      </table>
    </div>
  );
};

const EMPTY_ARRAY = [];

DataTable.propTypes = {
  stickyHeader: PropTypes.bool,
  hoverable: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  data: PropTypes.array,
  columns: PropTypes.array,
  tableOptions: PropTypes.object,
  emptyMessage: PropTypes.node,
  onRowClick: PropTypes.func,
  getRowProps: PropTypes.func,
};

DataTable.defaultProps = {
  stickyHeader: true,
  hoverable: true,
  children: null,
  className: '',
  data: undefined,
  columns: undefined,
  tableOptions: undefined,
  emptyMessage: 'No records found',
  onRowClick: undefined,
  getRowProps: undefined,
};

export default DataTable;
